import { isUsingNewComponents } from "../../add-element/utils";
import { TrackContext } from "../../contexts/TrackContext";
import createMemoizer from "../../core/dashboard/createMemoizer";
import { useScrollTo } from "../../hooks/useScrollTo";
import { buildRouteArgs } from "../../pages/utils";
import Paragraphs from "../../pages/website/Paragraphs";
import RefSource from "../../ref-monitor/RefSource";
import { hashPath } from "../../track/utils";
import { isRouteActive } from "../router.js";
import { selectAsset, selectSite } from "../selectors";
import AnnouncementBanner from "./AnnouncementBanner";
import { OI_UPLOAD_YOUR_OWN_IMAGE_SKUS } from "./EditDesign/constants";
import FancyClothesContext from "./FancyClothesContext";
import NavigatePreviewContext from "./NavigatePreviewContext";
import { css } from "@emotion/react";
import {
  LargeDivider,
  LargeGraphic,
  LargeHeadline,
  MobileLargeGraphic,
  WeddingHomeHorizontalFullHeader,
  WeddingHomeVerticalDoubleHeader,
  WeddingHomeVerticalHeader,
  WeddingHomeVerticalSingleHeader,
  withDesign,
} from "@minted/fancyclothes";
import {
  styleUtils,
  theme,
  useMediaQueryState,
} from "@minted/minted-components";
import { parse as parseDate, format as formatDate } from "date-fns";
import PropTypes from "prop-types";
import React, { Component, useContext } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { actions } from "redux-router5";

const headerStyles = {
  centered: css`
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x4};
    margin-right: auto;
    margin-left: auto;
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
    max-width: ${styleUtils.rem(780)};
    margin-bottom: ${styleUtils.rem(40)};
  `,
  fullWidth: css`
    width: 15%;
    align-self: center;
  `,
  image: css`
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    box-sizing: border-box;
    display: block;
    margin-bottom: ${styleUtils.rem(30)};
    max-height: ${styleUtils.rem(650)};
    object-fit: cover;
    width: 100%;
  `,
  wrapper: (isWedding) => css`
    margin-bottom: ${styleUtils.rem(isWedding ? 80 : 40)};
  `,
};

const couplePhotoLargeDefault = require("../images/website/photo-couple-wide.png");
const couplePhotoDefault = require("../images/website/photo-couple.png");
const singlePhotoDefault = require("../images/website/photo-single.png");
const uploadYourImageDefault = require("../images/website/upload-your-own.png");

const buildNavItems = (
  pages,
  router,
  navigateTo,
  isCustomizePage,
  usePreviewOverride,
  emitSiteEvent,
  scrollToId,
  setPreviewOverrideSlug,
  site,
  isMobile,
  isMultiPage,
  isPreview,
) => {
  const referrer = document.referrer;

  return pages.map((page) => ({
    active: isRouteActive({
      isCustomizePage,
      router,
      slug: page.slug,
    }),
    onClick: (event) => {
      event.preventDefault();
      emitSiteEvent({
        event: "page_load",
        feature: site.isDemoSite ? "site_demo" : "site_guest_experience",
        guest_id: localStorage.getItem("mintedWebsitesGuestId"),
        is_mobile: isMobile,
        referrer_domain: referrer.split("?")[0],
        referrer_hash: hashPath(referrer),
        tab_name: page.givenName,
        tab_slug: page.slug,
        url_hash: hashPath(window.location.href),
      });

      if (usePreviewOverride) {
        setPreviewOverrideSlug(page.slug);
      } else {
        if (!isMultiPage) {
          scrollToId(`page-${page.slug}`);
        }

        if (navigateTo) {
          const { name, params } = buildRouteArgs({
            data: page,
          });

          navigateTo(isPreview ? name : page.slug, params);
        }
      }
    },
    title: page.givenName,
    url: router.buildUrl(page.slug),
  }));
};

const renderHeader = (
  isMobile,
  siteDetails,
  site,
  assets,
  navItems,
  children,
  condensed,
  textAlignment,
  isPreview,
  breakpoint,
) => {
  if (siteDetails.isWedding) {
    let weddingDate;
    const eventDateHeading =
      siteDetails.eventDateHeading ||
      "Please join us for our wedding celebration on";

    if (siteDetails.weddingDate) {
      // change format to what fancyclothes requires
      try {
        const parsedDate = parseDate(
          siteDetails.weddingDate,
          "MM/dd/yyyy",
          new Date(),
        ); // Using new Date(site.weddingDate) was off by one day

        weddingDate = formatDate(parsedDate, "yyyy-MM-dd");
      } catch {
        weddingDate = `${new Date().getFullYear() + 1}-08-01`;
      }
    } else {
      weddingDate = `${new Date().getFullYear() + 1}-08-01`;
    }
    const commonProps = {
      bottomName: siteDetails.spouseName || "Name",
      condensed,
      countdownText: siteDetails.countdownText || "the big day",
      eventDateHeading,
      introHeading: siteDetails?.isOurStoryVisible
        ? siteDetails.welcomeHeading || "Our Story"
        : null,
      isCountdownVisible: siteDetails.isCountdownVisible,
      isOurStoryVisible: siteDetails?.isOurStoryVisible,
      isWeddingDateVisible:
        siteDetails.isWeddingDateVisible && Boolean(siteDetails.weddingDate),
      leftName: siteDetails.yourName || "Name",
      nameSeparator: siteDetails.nameSeparator,
      navItems,
      rightName: siteDetails.spouseName || "Name",
      textAlignment,
      topName: siteDetails.yourName || "Name",
      weddingDate,
    };

    switch (siteDetails.themeIdentifier) {
      case "l1":
        return (
          <WeddingHomeVerticalSingleHeader
            photoURL={assets.themeImage || couplePhotoDefault}
            {...commonProps}
          >
            {children}
          </WeddingHomeVerticalSingleHeader>
        );
      case "l2":
        return (
          <WeddingHomeVerticalDoubleHeader
            leftPhotoURL={assets.brideImage || singlePhotoDefault}
            rightPhotoURL={assets.groomImage || singlePhotoDefault}
            {...commonProps}
            eventDateHeading={eventDateHeading + " "} // Needs space for inline heading + date
          >
            {children}
          </WeddingHomeVerticalDoubleHeader>
        );
      case "l3":
        return (
          <WeddingHomeHorizontalFullHeader
            photoURL={assets.themeImage || couplePhotoLargeDefault}
            {...commonProps}
          >
            {children}
          </WeddingHomeHorizontalFullHeader>
        );
      case "l4":
        return (
          <WeddingHomeVerticalHeader {...commonProps}>
            {children}
          </WeddingHomeVerticalHeader>
        );
    }
  } else if (siteDetails.isOnlineInvitation) {
    const isMobileInPreview = isPreview && breakpoint === "xs";

    return (
      <div>
        {site.isOnlineInvitation &&
        OI_UPLOAD_YOUR_OWN_IMAGE_SKUS.has(site.mintedProductSku) ? (
          <img
            alt={siteDetails.eventHeading || ""}
            css={headerStyles.image}
            data-cy="mainImage"
            src={assets?.themeImage || uploadYourImageDefault}
          />
        ) : isMobile || isMobileInPreview ? (
          <MobileLargeGraphic text="Online invitation header image" />
        ) : (
          <LargeGraphic text="Online invitation header image" />
        )}

        <div css={headerStyles.centered}>
          <LargeHeadline>{siteDetails.eventHeading || ""}</LargeHeadline>
          <div css={headerStyles.fullWidth}>
            <hr />
          </div>
          <Paragraphs
            text={siteDetails.eventDescription || ""}
            textAlignment="center"
          />
        </div>
      </div>
    );
  }
};

@withDesign("HeaderDivider")
class HeaderDivider extends Component {
  render() {
    const { getRule, isMultiPage, style = {} } = this.props;
    const introBackgroundColor = getRule("backgroundColor", "IntroContainer");
    const introBackgroundPattern = getRule(
      "backgroundPattern",
      "IntroContainer",
    );
    const hasNoIntroBackground = !(
      introBackgroundColor || introBackgroundPattern
    );
    const shouldHaveSeparator = isMultiPage || hasNoIntroBackground;

    return (
      shouldHaveSeparator && (
        <div style={{ ...style }}>
          <LargeDivider />
        </div>
      )
    );
  }
}

const WebsiteHeader = (
  {
    assets,
    breakpoint,
    children,
    condensed,
    connectRef,
    isCustomizePage,
    isPreview,
    navigateTo,
    pages,
    site,
    siteDetails,
    textAlignment = "center",
    usePreviewOverride,
  },
  { router },
) => {
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "small",
  });
  const isMobile = mediumMediaQueryState === "BELOW";
  const emitSiteEvent = useContext(TrackContext);
  const setPreviewOverrideSlug = useContext(
    NavigatePreviewContext,
  )?.setPreviewOverrideSlug;
  const { scrollToId } = useScrollTo();
  const isMultiPage = site.pageConfiguration === "multi_page";

  const navItems = buildNavItems(
    pages,
    router,
    navigateTo,
    isCustomizePage,
    usePreviewOverride,
    emitSiteEvent,
    scrollToId,
    setPreviewOverrideSlug,
    site,
    isMobile,
    isMultiPage,
    isPreview,
  );

  const isHomePage = ["pages.home", "home"].includes(router.getState().name);

  return connectRef(
    "home",
    "pages",
    <div
      css={
        !isUsingNewComponents(site) &&
        headerStyles.wrapper(siteDetails.isWedding)
      }
    >
      {siteDetails.showAnnouncement && (
        <AnnouncementBanner
          bodyText={siteDetails.announcementBody}
          headingText={siteDetails.announcementHeading}
        />
      )}

      <FancyClothesContext.Consumer>
        {() => (
          <div>
            {renderHeader(
              isMobile,
              siteDetails,
              site,
              assets,
              navItems,
              children,
              condensed,
              textAlignment,
              isPreview,
              breakpoint,
            )}
            {!isUsingNewComponents(site) && (
              <HeaderDivider
                isMultiPage={isMultiPage}
                // TODO: change spacing using 'gap' instead of 'margin-bottom' inside the fancyclothes Wedding Headers, then remove this
                style={{
                  marginTop:
                    siteDetails.isWedding &&
                    !siteDetails.isOurStoryVisible &&
                    isHomePage &&
                    isMultiPage
                      ? 80
                      : 0,
                }}
              />
            )}
          </div>
        )}
      </FancyClothesContext.Consumer>
    </div>,
  );
};

WebsiteHeader.propTypes = {
  breakpoint: PropTypes.string,
  isCustomizePage: PropTypes.bool,
  isPreview: PropTypes.bool,
};

WebsiteHeader.contextTypes = {
  router: PropTypes.object.isRequired,
};

const memoizeAssets = createMemoizer();
const memoizeSite = createMemoizer();

const mapStateToProps = (state, props) => {
  const site = memoizeSite(selectSite(state.resources.sites));

  const assets = {};

  const siteDetails = {
    ...site,
  };

  if (isUsingNewComponents(site)) {
    [
      "announcementBody",
      "announcementHeading",
      "countdownText",
      "eventDateHeading",
      "isCountdownVisible",
      "isOurStoryVisible",
      "isWeddingDateVisible",
      "nameSeparator",
      "showAnnouncement",
      "spouseName",
      "weddingDate",
      "welcomeHeading",
      "welcomeText",
      "yourName",
    ].forEach((field) => {
      if (field in site.siteDetails) {
        siteDetails[field] = site.siteDetails[field];
      }
    });
  }

  switch (site.themeIdentifier) {
    case "l1":
      if (
        site.isWedding ||
        OI_UPLOAD_YOUR_OWN_IMAGE_SKUS.has(site.mintedProductSku)
      ) {
        assets.themeImage = selectAsset(
          state.resources,
          "themeImage",
          "crop1200650",
        );
      }
      break;
    case "l2":
      assets.brideImage = selectAsset(
        state.resources,
        "brideImage",
        "crop500500",
      );
      assets.groomImage = selectAsset(
        state.resources,
        "groomImage",
        "crop500500",
      );
      break;
    case "l3":
      assets.themeImage = selectAsset(
        state.resources,
        "themeImage",
        "crop16001067",
      );
      break;
    case "l4":
      break; // no images
  }

  return {
    assets: memoizeAssets(assets),
    children: props.children,
    condensed: props.condensed,
    pages: props.pages,
    site,
    siteDetails,
  };
};

const bindActions = (dispatch) => ({
  dispatch,
  navigateTo: bindActionCreators(actions.navigateTo, dispatch),
});

export default connect(
  mapStateToProps,
  bindActions,
)(RefSource(WebsiteHeader, "home"));
